import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Baby, Pencil, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DailyTipsAdmin() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(1);
  const [editDialog, setEditDialog] = useState({ open: false, tip: null });
  const [editForm, setEditForm] = useState({});
  const [total, setTotal] = useState(0);
  const [allTotal, setAllTotal] = useState(0);

  const fetchTips = (w) => {
    setLoading(true);
    api.get(`/api/admin/daily-tips?week=${w}`).then(res => {
      setTips(res.data.tips || []);
      setTotal(res.data.total || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { fetchTips(week); }, [week]);
  useEffect(() => { api.get('/api/admin/daily-tips').then(res => setAllTotal(res.data.total || 0)); }, []);

  const saveTip = async () => {
    try {
      await api.put(`/api/admin/daily-tips/${editDialog.tip.id}`, editForm);
      toast.success('Tip updated');
      setEditDialog({ open: false, tip: null });
      fetchTips(week);
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-semibold">Daily Tips</h1><p className="text-muted-foreground mt-1">{allTotal} tips across 40 weeks</p></div>
      </div>

      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => setWeek(Math.max(1, week - 1))} disabled={week <= 1}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="text-center">
          <p className="text-2xl font-semibold">Week {week}</p>
          <p className="text-xs text-muted-foreground">Trimester {week <= 12 ? 1 : week <= 26 ? 2 : 3}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setWeek(Math.min(40, week + 1))} disabled={week >= 40}><ChevronRight className="h-4 w-4" /></Button>
        <div className="ml-4">
          <Select value={String(week)} onValueChange={v => setWeek(parseInt(v))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({length: 40}, (_, i) => <SelectItem key={i+1} value={String(i+1)}>Week {i+1}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : tips.length === 0 ? (
        <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12"><Baby className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No tips for week {week}</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tips.map(tip => (
            <Card key={tip.id} className="rounded-2xl hover:shadow-md transition-shadow">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="secondary">Day {tip.day}</Badge>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditForm({...tip}); setEditDialog({ open: true, tip }); }}><Pencil className="h-3 w-3" /></Button>
                </div>
                <p className="text-sm font-semibold mb-1">Baby is the size of {tip.baby_size}</p>
                <p className="text-xs text-muted-foreground mb-2">{tip.baby_weight}</p>
                <p className="text-xs text-primary mb-2">{tip.baby_fact}</p>
                <div className="p-2 rounded-lg bg-muted/40">
                  <p className="text-xs font-medium">Daily Tip:</p>
                  <p className="text-xs text-muted-foreground">{tip.daily_tip}</p>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">SoulAI: "{tip.soulai_prompt}"</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editDialog.open} onOpenChange={o => setEditDialog({ open: o, tip: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit Tip — Week {editForm.week}, Day {editForm.day}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium mb-1 block">Baby Size</label><Input value={editForm.baby_size || ''} onChange={e => setEditForm({...editForm, baby_size: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Baby Weight</label><Input value={editForm.baby_weight || ''} onChange={e => setEditForm({...editForm, baby_weight: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Baby Fact</label><Textarea value={editForm.baby_fact || ''} onChange={e => setEditForm({...editForm, baby_fact: e.target.value})} rows={2} /></div>
            <div><label className="text-sm font-medium mb-1 block">Daily Tip</label><Textarea value={editForm.daily_tip || ''} onChange={e => setEditForm({...editForm, daily_tip: e.target.value})} rows={2} /></div>
            <div><label className="text-sm font-medium mb-1 block">SoulAI Prompt</label><Input value={editForm.soulai_prompt || ''} onChange={e => setEditForm({...editForm, soulai_prompt: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, tip: null })}>Cancel</Button>
            <Button onClick={saveTip}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
